// src/components/ChatWindow.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  CircularProgress,
  Tooltip,
  Chip,
  InputAdornment,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import { API_URL } from "../../../../globals/utils/constants";

const ChatWindow = ({ username, onClose, bondId, role }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(true); // Single loading state
  const ws = useRef(null);
  const messagesEndRef = useRef(null);
  const lastOptimisticRef = useRef(null);

  // Helper: instantly jump chat to the bottom
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "auto" });
    }
  };

  // 1) Fetch old messages from REST endpoint
  useEffect(() => {
    async function fetchOldMessages() {
      try {
        const response = await fetch(`${API_URL}/messages/${bondId}`);
        if (!response.ok) {
          throw new Error("Failed to fetch old messages");
        }
        const data = await response.json();
        const formatted = data.map((msg) => {
          const isCurrentUser = msg.sender_username === username;
          return {
            displayName: isCurrentUser
              ? "You"
              : `${msg.sender_name || "Unknown"}`,
            sender_type: msg.sender_type,
            actualUsername: msg.sender_username,
            content: msg.message,
            timestamp: new Date(msg.created_at).toLocaleTimeString(),
          };
        });
        setMessages(formatted);
        scrollToBottom(); // scroll while still loading
        setLoading(false);
      } catch (error) {
        console.error("Error fetching old messages:", error);
        setLoading(false);
      }
    }
    fetchOldMessages();
  }, [bondId, username]);

  // 2) Open WebSocket connection for new messages
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    const url = API_URL.replace(/^https?:\/\//, '');
    const wsProtocol = window.location.protocol === "https:" ? "wss" : "ws";
    let wsUrl = `${wsProtocol}://${url}/ws/chat/${bondId}/`;
    if (token) {
      wsUrl += `?token=${token}`;
    }
    ws.current = new WebSocket(wsUrl);

    ws.current.onopen = () => {
      console.log("WebSocket connected");
    };

    ws.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.message) {
          // If message is from current user and matches optimistic update, skip duplicate.
          if (
            data.sender_username === username &&
            lastOptimisticRef.current &&
            data.message === lastOptimisticRef.current
          ) {
            lastOptimisticRef.current = null;
            return;
          }
          setMessages((prev) => [
            ...prev,
            {
              displayName:
                data.sender_username === username
                  ? "You"
                  : `${data.sender_name || "Unknown"}`,
              sender_type: data.sender_type,
              actualUsername: data.sender_username,
              content: data.message,
              timestamp: new Date().toLocaleTimeString(),
            },
          ]);
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    ws.current.onclose = () => {
      console.log("WebSocket disconnected");
    };

    ws.current.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [bondId, username]);

  // Scroll to bottom whenever messages update
  useEffect(() => {
    if (!loading) {
      scrollToBottom();
    }
  }, [messages, loading]);

  // 3) Send new messages with an optimistic update
  const handleSendMessage = () => {
    if (inputMessage.trim() === "") return;
    const messageData = { message: inputMessage };

    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(messageData));
      lastOptimisticRef.current = inputMessage;
      setMessages((prev) => [
        ...prev,
        {
          displayName: "You",
          sender_type: role,
          actualUsername: username,
          content: inputMessage,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
      setInputMessage("");
      scrollToBottom();
    } else {
      console.error("WebSocket is not open. Current state:", ws.current?.readyState);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && inputMessage.trim()) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (loading) {
    return (
      <Paper
        sx={{
          position: "fixed",
          bottom: 80,
          right: 16,
          width: 350,
          height: 500,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1100,
          borderRadius: 3,
          overflow: "hidden",
          backdropFilter: "blur(8px)",
          backgroundColor: "#fff",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: 6,
        }}
      >
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        position: "fixed",
        bottom: 80,
        right: 16,
        width: 350,
        height: 500,
        display: "flex",
        flexDirection: "column",
        boxShadow: 6,
        zIndex: 1100,
        borderRadius: 3,
        overflow: "hidden",
        backdropFilter: "blur(8px)",
        backgroundColor: "#fff",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* Chat Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          background: "linear-gradient(135deg, #111 0%, #333 100%)",
          color: "common.white",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {role === "carrier_team_member" ? "Underwriting Chat" : "Underwriting Chat"}
          </Typography>
          <Chip label="Live" size="small" color="success" variant="filled" />
        </Box>
        <Tooltip title="Close">
          <IconButton onClick={onClose} size="small" sx={{ color: "inherit" }}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Chat Messages */}
      <Box sx={{ overflowY: "auto", flexGrow: 1, p: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
        {messages.map((msg, index) => {
          const isCurrentUser = msg.displayName === "You";
          const avatarText = msg.sender_type === "carrier_team_member" ? "UW" : "V";
          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: isCurrentUser ? "flex-end" : "flex-start",
                gap: 1,
              }}
            >
              {!isCurrentUser && (
                <Avatar sx={{ bgcolor: "#555", width: 28, height: 28, fontSize: 12, color: "#fff" }}>
                  {avatarText}
                </Avatar>
              )}
              <Box
                sx={{
                  maxWidth: "75%",
                  px: 1.5,
                  py: 1,
                  borderRadius: 3,
                  bgcolor: isCurrentUser ? "#111" : "#f2f2f2",
                  color: isCurrentUser ? "#fff" : "#111",
                  boxShadow: 1,
                }}
              >
                {!isCurrentUser && (
                  <Typography variant="caption" sx={{ opacity: 0.8, display: "block", mb: 0.25 }}>
                    {msg.displayName}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {msg.content}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.5,
                    textAlign: "right",
                    color: isCurrentUser ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                  }}
                >
                  {msg.timestamp}
                </Typography>
              </Box>
            </Box>
          );
        })}
        <div ref={messagesEndRef} />
      </Box>

      {/* Chat Input */}
      <Box sx={{ p: 1.5, pt: 1, borderTop: "1px solid", borderColor: "divider", backgroundColor: "#fff" }}>
        <TextField
          fullWidth
          placeholder="Type a message..."
          variant="outlined"
          size="small"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Send">
                  <span>
                    <IconButton onClick={handleSendMessage} disabled={!inputMessage.trim()} sx={{ color: "#111" }}>
                      <SendIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              backgroundColor: "rgba(255,255,255,0.95)",
            },
          }}
        />
      </Box>
    </Paper>
  );
};

export default ChatWindow;
